import React, { useState } from "react";
import { Search, X, ChevronDown, ChevronUp, LayoutDashboard } from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { JobDetailModal } from "../components/JobDetailModal";
import { WorkloadByJobChart } from "../components/WorkloadByJobChart";
import { ThroughputChart } from "../components/ThroughputChart";
import { TechKpiCards } from "../components/TechKpiCards";
import { WorkByTechnicianBoard } from "../components/WorkByTechnicianBoard";
import { AiCopilotPanel } from "../components/AiCopilotPanel";
import { technicianColor } from "../lib/techColors";
import { useFetch } from "../lib/useApi";

const KPI_LABELS = {
  received_today: "Received Today", received_week: "Received This Week",
  calibrated_today: "Calibrated Today", calibrated_week: "Calibrated This Week",
  awaiting_spares: "Awaiting Spares", awaiting_customer: "Awaiting Customer",
  unassigned_gt2: "Unassigned > 2 Days", overdue: "Overdue", due_for_calibration: "Due for Calibration",
};

const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Electronics", "Temperature", "Hydraulics", "Mass"];

// Technical Manager control centre — a SEPARATE operational/management view
// from the Job Flow Board (that board and its data/logic are untouched).
// Reuses the Job Flow Board's workload + throughput data/components, adds
// clickable KPI filters, a technician-first work board, and a read-only
// AI copilot grounded in the same live figures.
export default function TechnicalDashboard() {
  const [filters, setFilters] = useState({ discipline: null, technician_id: null, status: null, kpi: null, search: "" });
  const [openJobId, setOpenJobId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [dashboardOpen, setDashboardOpen] = useState(true);

  const { data: kpis } = useFetch("/technical-dashboard/kpis");
  const { data: board, loading: boardLoading, refetch: refetchBoard } = useFetch(
    "/technical-dashboard/lab-by-technician",
    { discipline: filters.discipline, technician_id: filters.technician_id, status: filters.status, kpi: filters.kpi, search: filters.search || undefined },
    [filters.discipline, filters.technician_id, filters.status, filters.kpi, filters.search]
  );

  const activeFilterChips = [
    filters.discipline && { key: "discipline", label: `Discipline: ${filters.discipline}` },
    filters.technician_id && { key: "technician_id", label: `Technician: ${filters.technician_id === "unassigned" ? "Unassigned" : filters.technician_id}` },
    filters.status && { key: "status", label: `Status: ${filters.status}` },
    filters.kpi && { key: "kpi", label: KPI_LABELS[filters.kpi] || filters.kpi },
  ].filter(Boolean);

  function clearFilter(key) {
    setFilters((f) => ({ ...f, [key]: key === "search" ? "" : null }));
  }

  function applyAiFilters(f) {
    setFilters((prev) => ({
      discipline: f.discipline ?? prev.discipline,
      technician_id: f.technician_id ?? prev.technician_id,
      status: f.status ?? prev.status,
      kpi: f.kpi ?? prev.kpi,
      search: f.search ?? prev.search,
    }));
  }

  return (
    <div data-testid="technical-dashboard-page">
      <PageHead
        crumb="Operations"
        title="Technical Dashboard"
        sub="Where demand is, who has the workload, and what needs attention today — separate from the Job Flow Board."
      />

      <div className="card pad" style={{ marginBottom: 18 }} data-testid="td-dashboard-section">
        <div className="sec-h" style={{ marginBottom: dashboardOpen ? 13 : 0, cursor: "pointer" }} onClick={() => setDashboardOpen((v) => !v)}>
          <h3><LayoutDashboard size={15} style={{ verticalAlign: -2, marginRight: 7, color: "var(--gold-deep)" }} />Dashboard Overview</h3>
          <button className="btn ghost sm" data-testid="td-dashboard-toggle" onClick={(e) => { e.stopPropagation(); setDashboardOpen((v) => !v); }}>
            {dashboardOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {dashboardOpen ? "Collapse" : "Expand"}
          </button>
        </div>
        {dashboardOpen && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} data-testid="td-dashboard-body">
            <WorkloadByJobChart
              colorForTech={technicianColor}
              onDisciplineClick={(d) => setFilters((f) => ({ ...f, discipline: f.discipline === d ? null : d }))}
              activeDiscipline={filters.discipline}
              inLabOnly
            />
            <ThroughputChart
              title="Received vs Calibrated"
              receivedColor="var(--gold-bright)"
              calibratedColor="var(--gold-deep)"
              dayOptions={[7, 30, 365]}
              showBacklog
            />
          </div>
        )}
      </div>

      <div style={{ marginBottom: 18 }}>
        <TechKpiCards kpis={kpis} activeKpi={filters.kpi} onSelect={(k) => setFilters((f) => ({ ...f, kpi: k }))} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, alignItems: "start" }}>
        <div className="card pad">
          <div className="sec-h">
            <h3>Work in the Lab by Technician</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select
                className="input" data-testid="td-discipline-filter"
                style={{ width: "auto", padding: "6px 9px", fontSize: 12.5 }}
                value={filters.discipline || ""}
                onChange={(e) => setFilters((f) => ({ ...f, discipline: e.target.value || null }))}
              >
                <option value="">All disciplines</option>
                {DISCIPLINES.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <div style={{ position: "relative" }}>
                <Search size={14} style={{ position: "absolute", left: 9, top: 9, color: "var(--text-3)" }} />
                <input
                  className="input" data-testid="td-search-input"
                  style={{ paddingLeft: 30, width: 200, fontSize: 12.5 }}
                  placeholder="Search DN, item, customer…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") setFilters((f) => ({ ...f, search: searchInput })); }}
                />
              </div>
              <button className="btn ghost sm" data-testid="td-search-btn" onClick={() => setFilters((f) => ({ ...f, search: searchInput }))}>Search</button>
            </div>
          </div>

          {!!activeFilterChips.length && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }} data-testid="td-active-filters">
              {activeFilterChips.map((c) => (
                <span key={c.key} className="tag-soft" style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
                  onClick={() => clearFilter(c.key)} data-testid={`td-filter-chip-${c.key}`}>
                  {c.label} <X size={11} />
                </span>
              ))}
            </div>
          )}

          <WorkByTechnicianBoard data={board} loading={boardLoading} onOpenItem={setOpenJobId} />
        </div>
      </div>

      <AiCopilotPanel onApplyFilters={applyAiFilters} />

      {openJobId && (
        <JobDetailModal jobId={openJobId} onClose={() => setOpenJobId(null)} onChanged={refetchBoard} />
      )}
    </div>
  );
}
